import { BlockerDialog } from '@sequencehq/core-components'
import { useLogEvent } from 'lib/postHog/useLogEvent'
import { FC, useEffect } from 'react'
import { useBlocker, Location } from 'react-router-dom'

type BlockerProps = {
  dirty: boolean
  submitting: boolean
  name: string
  ignoreBlock?: (nextLocation: Location, currentLocation?: Location) => boolean
  modalTitle?: string
  message?: string
  submitTitle?: string
  cancelTitle?: string
}

/**
 * Navigation state for flows which intentionally leave a dirty form/editor
 * and must not trigger the confirmation dialog. Pass as the `state` option
 * of `navigate`.
 */
export const IGNORE_BLOCKER_STATE = { ignoreBlocker: true }

/**
 * Blocks in-app navigation with a confirmation dialog while `dirty` is true
 * (and not `submitting`).
 *
 * Opting out of blocking:
 * - Navigations which set `ignoreBlocker: true` on their navigation state
 *   (use `navigate(to, { state: IGNORE_BLOCKER_STATE })`) are always let
 *   through without confirmation. Use this for flows which intentionally
 *   leave the screen, e.g. after archiving the entity being edited.
 * - `ignoreBlock` allows consumers to skip blocking based on the next and
 *   current locations (e.g. same-editor redirects). It is only consulted
 *   when the navigation state has not already opted out via `ignoreBlocker`.
 */
export const Blocker: FC<BlockerProps> = ({
  dirty,
  submitting,
  name,
  ignoreBlock,
  modalTitle = `Leave ${name} form?`,
  message = 'You have unsaved changes which will be lost',
  submitTitle = 'Leave',
  cancelTitle = 'Keep editing'
}) => {
  const { logEvent } = useLogEvent()
  const blocked = dirty && !submitting
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (nextLocation.state?.ignoreBlocker) {
      return false
    }

    if (ignoreBlock && ignoreBlock(nextLocation, currentLocation)) {
      return false
    }

    return blocked
  })

  useEffect(() => {
    if (blocker.state === 'blocked' && !blocked) {
      return blocker.reset()
    }
  }, [blocker, blocked])

  return (
    <BlockerDialog
      isOpen={blocker.state === 'blocked'}
      title={modalTitle}
      submitTitle={submitTitle}
      cancelTitle={cancelTitle}
      onLeave={() => {
        if (blocker.state !== 'blocked') {
          return
        }

        logEvent({
          eventName: 'blocker_abandoned_form',
          metadata: {
            formName: name
          }
        })
        blocker.proceed()
      }}
      onStay={() => {
        logEvent({
          eventName: 'blocker_go_back_to_form',
          metadata: {
            formName: name
          }
        })

        if (blocker.state === 'blocked') {
          blocker.reset()
        }
      }}
    >
      {message}
    </BlockerDialog>
  )
}
